import { useCallback, useMemo } from 'react';

import { AgGridServerTable } from 'core/components';
import { NON_EMPTY } from 'core/constants';
import { TableLayoutType } from 'core/enums';
import { AgGridServerTableProvider, SelectionCountProvider } from 'core/states';
import { AgGridGetRowsParams, ISelectionActionProps, ITableActionProps } from 'core/types';
import {
  RemovePricingProfilePartNumbersButton,
  UpsertPricingProfilePartNumberButton,
} from 'pricing/components';
import { PricingProfilePartNumberType } from 'pricing/enums';
import { partNumberService } from 'pricing/services';
import { IPricingProfile, IPricingProfilePartNumber } from 'pricing/types';

import { getColumnDef } from './columns';

interface IProps {
  pricingProfile: IPricingProfile;
  isReadOnly?: boolean;
}

export const PartNumberList = ({ pricingProfile, isReadOnly = false }: IProps) => {
  const columnDefs = useMemo(() => getColumnDef({ isEditable: !isReadOnly }), [isReadOnly]);

  const actionButtons = useCallback(
    ({ gridApi }: ITableActionProps) => {
      if (isReadOnly) {
        return [];
      }

      return [
        <UpsertPricingProfilePartNumberButton
          key="upsertPricingProfilePartNumberButton"
          structureId={pricingProfile.fixedPriceStructureId}
          onSubmitSuccess={() => gridApi?.refreshServerSide()}
        />,
      ];
    },
    [isReadOnly, pricingProfile.fixedPriceStructureId]
  );

  const selectionActionButtons = useCallback(
    ({ gridApi, selectedRows, deselectAllRows }: ISelectionActionProps) => {
      if (isReadOnly) {
        return [];
      }

      return [
        <RemovePricingProfilePartNumbersButton
          key="removeSelectedPartNumbersButton"
          pricingProfilePartNumbers={selectedRows as IPricingProfilePartNumber[]}
          onRemoveSuccess={() => {
            deselectAllRows();
            gridApi?.refreshServerSide();
          }}
        />,
      ];
    },
    [isReadOnly]
  );

  const getRows = useCallback(
    ({ filters, ...rest }: AgGridGetRowsParams) => {
      const { pricingType, ...restFilters } = filters;

      const pricingTypeFilter = pricingType as string[] | undefined;

      if (
        pricingTypeFilter?.length === 1 &&
        pricingTypeFilter[0] === PricingProfilePartNumberType.PRICE
      ) {
        restFilters.price = NON_EMPTY;
      }

      if (
        pricingTypeFilter?.length === 1 &&
        pricingTypeFilter[0] === PricingProfilePartNumberType.MARGIN
      ) {
        restFilters.margin = NON_EMPTY;
      }

      return partNumberService.getPartNumbers({
        filters: {
          ...restFilters,
          structureId: pricingProfile.fixedPriceStructureId,
        },
        ...rest,
      });
    },
    [pricingProfile.fixedPriceStructureId]
  );

  return (
    <SelectionCountProvider>
      <AgGridServerTableProvider
        tableId="pricingProfilePartNumbersTable"
        entityType="PartNumber"
        columnDefs={columnDefs}
        getRows={getRows}
        hasLayoutActions
        hasManageFilters
        isRowSelectable={() => !isReadOnly}
        layoutType={TableLayoutType.PRICING_PROFILE_PART_NUMBER}
      >
        <AgGridServerTable
          actionButtons={actionButtons}
          selectionActionButtons={selectionActionButtons}
        />
      </AgGridServerTableProvider>
    </SelectionCountProvider>
  );
};
